import { generateContentWithFallback, isGeminiConfigured } from './geminiClient'

/**
 * Cheap "does this reply answer the open question?" classifier.
 *
 * Used by the AI Magic CREATE tab: when Line has an open clarification and the
 * user types a follow-up, we ask gemini-2.5-flash a single yes/no so we can
 * either (a) wrap the reply as a [LINE_CLARIFICATION_RESPONSE] envelope or
 * (b) surface a "drop the open question and start fresh?" confirm chip when
 * the reply is clearly off-topic.
 *
 * Failure mode: if the call errors or returns garbage, default to TREAT_AS_ANSWER
 * — i.e. preserve current behavior (wrap the envelope). False positives are
 * preferable to false negatives here: confirming "are you switching topics?"
 * for a real answer wastes a click; treating a topic switch as an answer
 * confuses the router silently.
 */
export type ClarificationRelevance = 'ANSWERS_QUESTION' | 'OFF_TOPIC'

const PROMPT = (question: string, reply: string) => `You are a binary classifier. The assistant asked the user a clarifying question. Decide whether the user's reply is an answer to THAT question, or a topic shift away from it.

Question: ${question}
User reply: ${reply}

Return exactly one of these two words and nothing else:
ANSWERS_QUESTION
OFF_TOPIC`.trim()

export const classifyClarificationRelevance = async (
  question: string,
  reply: string,
  signal?: AbortSignal,
): Promise<ClarificationRelevance> => {
  if (!isGeminiConfigured() || !question.trim() || !reply.trim()) return 'ANSWERS_QUESTION'
  try {
    const result = await generateContentWithFallback({
      model: 'gemini-2.5-flash',
      payload: [PROMPT(question, reply)],
      generationConfig: { temperature: 0, responseMimeType: 'text/plain' },
      signal,
    })
    const verdict = result.text.trim().toUpperCase()
    return verdict.startsWith('OFF_TOPIC') ? 'OFF_TOPIC' : 'ANSWERS_QUESTION'
  } catch {
    return 'ANSWERS_QUESTION'
  }
}
